import _DatabaseModel, { i18n } from "./_DatabaseModel";

const ASSETS_TAB_CALENDAR = 22;
const ASSETS_TAB_DOCUMENTS = 23;
const ASSETS_TAB_EXTERNAL_GUIDS = 24;
const ASSETS_TAB_ISSUES = 25;
const ASSETS_TAB_LOCATIONS = 26;
const ASSETS_TAB_CONTROLS = 181;
const ASSETS_TAB_NETWORK_GRAPH = 27;
const ASSETS_TAB_PERSONS = 28;
const ASSETS_TAB_SERVICES = 29;
const ASSETS_TAB_TIMELINE = 30;
// const ASSETS_TAB_WORK_REPORTS = 31;
const ASSETS_TAB_RULES = 167;

class Asset extends _DatabaseModel {
  static model = "asset";
  static showSize = 7;
  static autofillParent = true;
  static editDialogComponent = "persons-and-assets-edit-dialog";

  static bottomNav = {
    mobile: ["card", "tree", "map"],
    desktop: ["list", "tree", "map"],
  };

  static createNewObject = {
    object: {
      category_id: null,
      cia: {},
    },
    params: { direct: true },
  };

  static tableHeaders = [
    { textKey: "form.label.name", value: "name" },
    { text: "", value: "tags", sortable: false },
    {
      textKey: "category.category",
      class: "text-no-wrap",
      value: "category.name",
    },
    { textKey: "form.label.archived_at", value: "discarded_at" },
  ];

  static show = {
    buttons: ["edit"],
    reloadingModels: [
      "person_asset",
      "location",
      "external_guid",
      "asset_link",
    ],
  };

  static filter = {
    parts: {
      fields: [
        "name",
        "id",
        "asset_group_id",
        "parent_id",
        "category_id",
        "tag_definitions",
        "detail",
        "persistence",
      ],
      extended_fields: [
        "description",
        "person_relation_id",
        "person_id",
        "service_id",
        "connector_id",
        "rm_asset_type",
        "confidentiality",
        "integrity",
        "availability",
        "time_filter",
      ],
      time_filter: ["created_at", "updated_at"],
    },
  };

  static operations = [
    "create_issue",
    "export",
    "report",
    "link_to_document",
    "change_parent",
    "tag",
    "untag",
    "group",
    "ungroup",
    "link_assets_to_person",
    "remove_assets_from_person",
    "archive_multiple",
    "destroy_multiple",
  ];

  static confidentiality_types = [
    "public",
    "internal",
    "protected",
    "strictly_protected",
  ];
  static integrity_types = ["low", "medium", "high"];
  static availability_types = ["low", "medium", "high"];
  static rm_asset_types = ["primary", "supporting"];

  static cardFields(object) {
    return {
      title: object.name,
      subtitle: object.category?.name,
      src: object,
      tags: true,
    };
  }

  static basicInfo(object) {
    return [
      {
        labelKey: "category.category",
        value: object.category,
        type: "link",
        to: "asset_categories",
      },
      {
        labelKey: "asset_group.asset_groups",
        value: object.asset_groups?.map((ag) => ag.name).join(", "),
        type: "plain",
      },
      {
        labelKey: "form.label.created_at",
        value: object.created_at,
        type: "date_time",
      },
      {
        labelKey: "form.label.archived_at",
        value: object.discarded_at,
        type: "date_time",
        condition: object.discarded_at,
      },
      {
        labelKey: "asset.label.type",
        value: object.category?.rm_asset_type
          ? i18n.t(`asset.rm_asset_types.${object.category.rm_asset_type}`)
          : "",
        type: "plain",
        condition: object.category?.rm_asset_type,
      },
      {
        labelKey: "form.label.confidentiality",
        value:
          object.cia && object.cia.confidentiality
            ? i18n.t(
                `asset.confidentiality_types.${object.cia.confidentiality}`
              )
            : "",
        type: "plain",
        condition: object.category?.rm_asset_type,
      },
      {
        labelKey: "form.label.integrity",
        value:
          object.cia && object.cia.integrity
            ? i18n.t(`asset.integrity_types.${object.cia.integrity}`)
            : "",
        type: "plain",
        condition: object.category?.rm_asset_type,
      },
      {
        labelKey: "form.label.availability",
        value:
          object.cia && object.cia.availability
            ? i18n.t(`asset.availability_types.${object.cia.availability}`)
            : "",
        type: "plain",
        condition: object.category?.rm_asset_type,
      },
    ];
  }

  static submenuActions = [
    this.commonActions.duplicate,
    this.commonActions.createIssue,
    this.commonActions.manageSubscriptions,
    this.commonActions.permittedPersons,
    this.commonActions.openActivities,
    this.commonActions.archive,
  ];

  static archivedActions = [
    this.commonActions.restore,
    this.commonActions.delete,
  ];

  static tabs = [
    {
      titleKey: "template.detail",
      component: "TemplatesDetailTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "issue.label.timeline",
      permission: ASSETS_TAB_TIMELINE,
      component: "TimelineTab",
      disabled: () => false,
    },
    {
      titleKey: "risk.label.network_graph",
      permission: ASSETS_TAB_NETWORK_GRAPH,
      component: "AssetNetworkGraphTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "document.documents",
      permission: ASSETS_TAB_DOCUMENTS,
      component: "DocumentsTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "person.persons",
      permission: ASSETS_TAB_PERSONS,
      component: "PersonAssetsTab",
      parameters: { target_entity: "person" },
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "service.services",
      permission: ASSETS_TAB_SERVICES,
      component: "ServicesOnAssetTab",
      disabled: () => false,
    },
    {
      titleKey: "rule.rules",
      permission: ASSETS_TAB_RULES,
      component: "RulesOnAssetTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "issue.issues",
      permission: ASSETS_TAB_ISSUES,
      component: "IssuesTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "control.controls",
      permission: ASSETS_TAB_CONTROLS,
      component: "ControlsTab",
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "calendar.calendar",
      permission: ASSETS_TAB_CALENDAR,
      component: "CalendarTab",
      disabled: () => false,
    },
    {
      titleKey: "location.location",
      permission: ASSETS_TAB_LOCATIONS,
      component: "LocationsTab",
      parameters: { entity_type: "Asset" },
      disabled: this.defaultDisabledTab(),
    },
    {
      titleKey: "external_guid.external_guids",
      permission: ASSETS_TAB_EXTERNAL_GUIDS,
      component: "ExternalGuidsTab",
      disabled: this.defaultDisabledTab(),
    },
    // {
    //   titleKey: "work_report.work_reports",
    //   permission: ASSETS_TAB_WORK_REPORTS,
    //   component: "WorkReportsOnAssetsPersonsTab",
    //   disabled: () => false,
    // },
  ];
}

export default Asset;
